import { useCallback, useState } from "react"

import { toast } from "@coval/ui"

import type { IVRFlow } from "@/types/IVRFlow"

import { isIVRFlow, validateIVRFlow } from "./ivr-flow-utils"

interface IVRJsonEditorApi {
  jsonDraft: string
  jsonError: string | null
  jsonIssues: string[]
  /** Update the draft text, clearing any prior error/issues (typing resets the
   * displayed failure). */
  setJsonDraft: (value: string) => void
  /** Serialize a flow into the draft and clear errors — used when entering the
   * JSON tab so the editor reflects current state. */
  syncFromFlow: (flow: IVRFlow) => void
  /** Reset the editor to empty with no errors. */
  reset: () => void
  /** Parse + validate the draft and, on success, hand the flow to `onApply`.
   * Surfaces parse/shape/validation failures via the error/issues state. */
  applyJsonDraft: () => void
}

/**
 * Owns the raw-JSON editor tab: the draft text plus parse/validation error
 * state. Applying a valid draft is delegated to the caller via `onApply` so
 * this hook never touches the canonical flow state directly.
 */
export const useIVRJsonEditor = (
  onApply: (flow: IVRFlow) => void,
): IVRJsonEditorApi => {
  const [jsonDraft, setJsonDraftState] = useState("")
  const [jsonError, setJsonError] = useState<string | null>(null)
  const [jsonIssues, setJsonIssues] = useState<string[]>([])

  const setJsonDraft = useCallback((value: string) => {
    setJsonDraftState(value)
    setJsonError(null)
    setJsonIssues([])
  }, [])

  const syncFromFlow = useCallback((flow: IVRFlow) => {
    setJsonDraftState(JSON.stringify(flow, null, 2))
    setJsonError(null)
    setJsonIssues([])
  }, [])

  const reset = useCallback(() => {
    setJsonDraftState("")
    setJsonError(null)
    setJsonIssues([])
  }, [])

  const applyJsonDraft = useCallback(() => {
    setJsonError(null)
    setJsonIssues([])
    let parsed: unknown
    try {
      parsed = JSON.parse(jsonDraft)
    } catch (err) {
      setJsonError(
        `Invalid JSON: ${err instanceof Error ? err.message : String(err)}`,
      )
      return
    }
    if (!isIVRFlow(parsed)) {
      setJsonError("JSON does not match the IVR flow shape.")
      return
    }
    const result = validateIVRFlow(parsed)
    if (!result.valid) {
      setJsonError("IVR flow validation failed:")
      setJsonIssues(result.issues)
      return
    }
    onApply(parsed)
    toast.success({ title: "IVR flow updated" })
  }, [jsonDraft, onApply])

  return {
    jsonDraft,
    jsonError,
    jsonIssues,
    setJsonDraft,
    syncFromFlow,
    reset,
    applyJsonDraft,
  }
}
